import React, {useState} from 'react'
import {
    Box,
    Flex,
    useStyleConfig,
    Text,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import {Box as ChakraBox, useBreakpointValue} from '@chakra-ui/react'
import useDamImage from '../../../../custom-hooks/use-dam-image'
import {getGlobalLink} from '../../../../utils/contentstack-utils'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useGetCategoriesData} from '../../../../custom-api/hooks/use-get-categories-data'
import {ChevronRight} from '../../../icons'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {remCalc} from '../../../../theme/_utils'

const TopCard = ({card}) => {
    const {locale} = useMultiSite()
    const categoriesData = useGetCategoriesData()
    const backgroundImage = useDamImage(card?.image)
    const navigate = useNavigation()
    const styles = useStyleConfig('PlpTopCards', {})
    const [isHovered, setIsHovered] = useState(false)
    const [isFocused, setIsFocused] = useState(false)
    const isMobile = useBreakpointValue({base: true, md: true, lg: false}) ?? true

    const {url, isAbsolute, label} =
        getGlobalLink({
            link: card?.cta,
            categoriesData: categoriesData,
            locale: locale?.alias,
        }) || {}

    const handleButtonClick = () => {
        if (!url) {
            return
        }

        navigate(url)
    }

    const handleAbsoluteLinkKeyDown = (event) => {
        if (event.key !== ' ' && event.key !== 'Spacebar') {
            return
        }

        event.preventDefault()
        event.currentTarget.click()
    }

    const shouldShowButton = isMobile || isHovered || isFocused

    const blurBg = backgroundImage?.url ? `url(${backgroundImage.url})` : undefined

    const sovraTitleColor =
        card?.sovra_title_hex_color && card?.sovra_title_hex_color !== ''
            ? card.sovra_title_hex_color
            : 'foreground.neutral.inverse'
    const titleColor =
        card?.sovra_title_hex_color && card?.title_hex_color !== ''
            ? card.title_hex_color
            : 'foreground.neutral.inverse'

    return (
        <Box
            {...styles?.topCardContainer}
            backgroundColor="lightgray"
            backgroundImage={blurBg}
            backgroundPosition="50%"
            backgroundRepeat="no-repeat"
            backgroundSize={isHovered ? '110%' : '100%'}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            transition="background-size 0.4s ease"
            as={isAbsolute ? 'a' : 'button'}
            {...(isAbsolute
                ? {
                      href: url,
                      target: '_blank',
                      rel: 'noopener noreferrer',
                      onKeyDown: handleAbsoluteLinkKeyDown,
                  }
                : {
                      type: 'button',
                      onClick: handleButtonClick,
                  })}
        >
            {blurBg && (
                <Box {...styles?.blurStack}>
                    <Box
                        {...styles?.blurSnapshot}
                        backgroundImage={blurBg}
                        sx={{
                            filter: `blur(${remCalc(10)})`,
                            transform: 'scale(1.01)',
                            maskImage: 'linear-gradient(transparent 45%, black 75%)',
                            WebkitMaskImage: 'linear-gradient(transparent 45%, black 75%)',
                        }}
                    />
                </Box>
            )}
            <Box {...styles?.bottomScrim} />
            <Flex {...styles?.cardContentContainer}>
                <Flex data-testid="card-text-container" {...styles?.contentContainer}>
                    {card?.sovra_title && (
                        <ChakraBox tabIndex={0} aria-label={card?.sovra_title}>
                            <Text as="h2" {...styles?.sovraTitle} color={sovraTitleColor}>
                                {card?.sovra_title}
                            </Text>
                        </ChakraBox>
                    )}
                    {card?.title && (
                        <ChakraBox tabIndex={0} aria-label={card?.title}>
                            <Text {...styles?.title} color={titleColor}>
                                {card?.title}
                            </Text>
                        </ChakraBox>
                    )}
                </Flex>
                <Flex
                    {...styles?.buttonContainer}
                    opacity={shouldShowButton ? 0.5 : 0}
                    transition="opacity 0.2s ease"
                    pointerEvents={shouldShowButton ? 'auto' : 'none'}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    aria-label={label}
                    {...(isAbsolute
                        ? {
                              href: url,
                              target: '_blank',
                              rel: 'noopener noreferrer',
                              onKeyDown: handleAbsoluteLinkKeyDown,
                          }
                        : {
                              type: 'button',
                              onClick: handleButtonClick,
                          })}
                >
                    <ChevronRight {...styles?.chevronIcon} />
                </Flex>
            </Flex>
        </Box>
    )
}

TopCard.propTypes = {
    card: PropTypes.shape({
        image: PropTypes.object,
        cta: PropTypes.object,
        sovra_title: PropTypes.string,
        sovra_title_hex_color: PropTypes.string,
        title: PropTypes.string,
        title_hex_color: PropTypes.string,
    }),
}

export default TopCard
